/**
 * Local helper to provide enriched metadata for traffic signals.
 * This acts as a database for per-intersection customization (e.g., custom HUD thresholds).
 * 
 * Future: This data could be fetched from the backend API.
 */

// Dictionary of overrides keyed by LIVA ID (liva_nro)
const signalMetadata = {
    // Example: specific intersection needs earlier warning
    // 'TRE123': {
    //     hudDistanceThreshold: 300, // meters
    //     hudTimeThreshold: 20 // seconds
    // }

    // Example: Directional Filtering (Vector-based)
    // 'TRE456': {
    //     // Array of rules. First matching rule applies.
    //     signalRules: [
    //         {
    //             description: "Coming from North, Turning East",
    //             entry: [340, 20], // Range [start, end] (crossing 0 handled)
    //             exit: [70, 110],
    //             signals: [3]
    //         },
    //         {
    //             description: "Coming from North, Going Straight",
    //             entry: [340, 20],
    //             exit: [160, 200],
    //             signals: [1, 2]
    //         }
    //     ]
    // }
};

/**
 * Get metadata for a specific signal ID.
 * Returns null if no metadata exists.
 * 
 * @param {string} signalId - The LIVA ID of the signal
 * @returns {Object|null} - Metadata object or null
 */
export const getSignalMetadata = (signalId) => {
    if (!signalId || !signalMetadata[signalId]) {
        return null;
    }
    return signalMetadata[signalId];
};

/**
 * Check if a bearing is within a range [start, end].
 * Handles wrapping around 360/0.
 */
const isBearingInRange = (bearing, [start, end]) => {
    if (start <= end) {
        return bearing >= start && bearing <= end;
    } else {
        // Wraps around 0 (e.g. 350 to 10)
        return bearing >= start || bearing <= end;
    }
};

/**
 * Filter signals based on entry/exit vectors and metadata rules.
 * 
 * @param {Array} signals - Array of signal objects (from API/deviceState)
 * @param {string} signalId - The LIVA ID of the intersection
 * @param {number} entryHeading - User's bearing entering the intersection
 * @param {number} exitHeading - Route's bearing exiting the intersection
 * @returns {Array} - The filtered list of signals to display
 */
export const filterSignalsByHeading = (signals, signalId, entryHeading, exitHeading) => {
    if (!signals || !signals.length) return [];

    // If we lack vector info, return all signals (fail safe)
    if (entryHeading == null || exitHeading == null) return signals;

    const metadata = getSignalMetadata(signalId);
    if (!metadata || !metadata.signalRules) {
        return signals; // No rules? Show all.
    }

    // Find the first rule (or all rules?) that matches the vector.
    // Assuming one maneuver = one set of signals.
    const matchingRule = metadata.signalRules.find(rule => {
        const entryMatch = !rule.entry || isBearingInRange(entryHeading, rule.entry);
        const exitMatch = !rule.exit || isBearingInRange(exitHeading, rule.exit);
        return entryMatch && exitMatch;
    });

    if (matchingRule) {
        // indices are assumed to be 0-based index in the 'signals' array? 
        // OR 'idx' property of the signal object?
        // Let's assume matches 'idx' property of signal object if available, 
        // matching the previous 'laneLogic' assumption is safest if we clarify 'idx'.
        // User example was simple indices.
        // Let's assume indices in the passed 'signals' array for now as typical "Vehicle Signal" rows are just arrays.
        // ACTUALLY: The 'signals' passed in are ALL signals (vehicle + priority).
        // It's safer if 'signals' in rule refers to the 'idx' (integer id) of the signal if available, or just filtering.
        // Let's filter by index in the array for now, consistent with previous implementation.
        return signals.filter((_, i) => matchingRule.signals.includes(i));
    }

    // If no rule matches this specific maneuver, maybe show NONE? or ALL?
    // "Default" behavior for unmatched maneuvers:
    // If rules exist for this intersection, but none match our maneuver -> likely we shouldn't show ANY signals (off-path / not covered).
    // But safely, maybe show all?
    // User said: "only the light relevant... should be considered".
    // Implies filtering OUT irrelevant ones.
    // Let's return ALL if no specific rule is found, to avoid hiding signals on undefined approaches.
    return signals;
};
